/**
 * Sprint 1069.D2 — Stale-rows drawer for breadcrumb / nav counter pills.
 *
 * Tiny popover that opens on counter-pill click. Fetches the top-3 oldest-
 * updated rows for the (mission, stage) pair and renders them as clickable
 * cards. Click row → navigate to the editor / list filter.
 *
 * Auto-closes on outside click + Esc. ~120 LOC budget.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Clock, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";

// Map stage → URL builder for "open this row".
const ROW_HREF_BUILDERS = {
  lead:     (r) => `/leads/${r.lead_id || ""}`,
  concept:  (r) => `/briefs/${r.brief_id || ""}`,
  quote:    (r) => `/quotes/${r.quote_id || ""}`,
  proposal: (r) => `/proposal-builder/${r.proposal_id || ""}`,
  // Sprint 2026-06-12 PM-10.5 — pitch decks live in the unified `/one-page-pitches`
  // listing (filtered by type=deck). There is no standalone `/pitch-decks` route.
  pitch:    (r) => `/one-page-pitches?type=deck&id=${r.deck_id || ""}`,
  production: (r) => `/projects/${r.project_id || ""}`,
  deliver:  (r) => `/deliveries/${r.delivery_id || ""}`,
  invoice:  (r) => `/admin/invoices/${r.invoice_id || ""}`,
  // Sprint 1069.E1-followup — Grow-the-Brand actionable stages.
  create:   (r) => `/creative-studio?section=social&post=${r.post_id || ""}`,
  publish:  (r) => `/creative-studio?section=social&tab=calendar&post=${r.post_id || ""}`,
};

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "—";
  const diff = Date.now() - t;
  const days = Math.floor(diff / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function rowTitle(stage, r) {
  if (stage === "lead") return r.name || r.company || "Lead";
  if (stage === "quote" || stage === "proposal" || stage === "pitch")
    return r.client_name || r.title || "—";
  if (stage === "concept") return r.title || r.client_name || "Brief";
  if (stage === "production") return r.name || r.client_name || "Project";
  if (stage === "deliver") return r.title || r.client_name || "Delivery";
  if (stage === "invoice") return r.client_name || `Invoice ${r.invoice_id || ""}`;
  if (stage === "create" || stage === "publish")
    return r.title || (r.content ? String(r.content).slice(0, 60) : "Untitled post");
  return r.title || "—";
}

function rowSub(stage, r) {
  if (stage === "quote" || stage === "invoice") {
    const v = r.total || r.amount;
    return v ? `$${Math.round(v).toLocaleString()}` : "";
  }
  if (stage === "lead") return r.company || r.status || "";
  if (stage === "create" || stage === "publish") return r.channel || r.status || "";
  return r.title || r.status || "";
}

export default function StaleRowsDrawer({ mission, stage, count, anchorRef, onClose }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const popoverRef = useRef(null);
  // Sprint 1069.D2 follow-up — portal to body + fixed coords so we escape
  // the FlowBreadcrumb's overflow-x-auto clipping (which was hiding the
  // drawer behind the Welcome Back card).
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = 288; // matches w-72
    // Clamp to viewport so it never falls off the right edge.
    const left = Math.max(8, Math.min(window.innerWidth - w - 8, r.left));
    setPos({ top: r.bottom + 6, left });
  }, [anchorRef]);

  useEffect(() => {
    api.get(`/loop/stage-stale-rows?mission=${mission}&stage=${stage}&limit=3`)
      .then(r => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [mission, stage]);

  // Close on outside click + Esc.
  useEffect(() => {
    const onDocClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  const open = (r) => {
    const href = ROW_HREF_BUILDERS[stage]?.(r);
    if (href) {
      onClose?.();
      navigate(href);
    }
  };

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[1000] w-72 rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] shadow-2xl p-3"
      style={{ top: pos.top, left: pos.left }}
      onClick={(e) => e.stopPropagation()}
      data-testid={`stale-rows-drawer-${stage}`}
      data-mission={mission}
      data-stage={stage}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[var(--app-text-muted)] flex items-center gap-1">
          <Clock className="w-3 h-3" /> {t("Oldest at this stage")}
        </p>
        <button
          onClick={onClose}
          className="p-0.5 rounded text-[var(--app-text-muted)] hover:text-[var(--app-text)]"
          aria-label="Close"
          data-testid={`stale-rows-close-${stage}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-[var(--app-text-muted)]" />
        </div>
      ) : (rows || []).length === 0 ? (
        <p className="text-xs text-[var(--app-text-muted)] py-3 text-center">
          {t("No items here right now.")}
        </p>
      ) : (
        <>
          <ul className="space-y-1.5">
            {rows.map((r, i) => (
              <li key={i}>
                <button
                  onClick={() => open(r)}
                  className="w-full text-left rounded-lg border border-[var(--app-border)] bg-[var(--app-bg)] px-2.5 py-2 hover:border-blue-500/40 transition-colors flex items-center gap-2"
                  data-testid={`stale-row-${stage}-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[var(--app-text)] truncate">{rowTitle(stage, r)}</p>
                    <p className="text-[10px] text-[var(--app-text-muted)] truncate">
                      {rowSub(stage, r)}
                      {rowSub(stage, r) && " · "}
                      {relativeTime(r.updated_at)}
                    </p>
                  </div>
                  <ArrowRight className="w-3 h-3 text-[var(--app-text-muted)] shrink-0" />
                </button>
              </li>
            ))}
          </ul>
          {count > 3 && (
            <p className="text-[10px] text-[var(--app-text-muted)] text-center mt-2 pt-2 border-t border-[var(--app-border)]">
              {t("Showing 3 of")} <span className="font-bold text-[var(--app-text)]">{count}</span> · {t("open the section to see all")}
            </p>
          )}
        </>
      )}
    </div>,
    document.body
  );
}
